import { useCallback, useMemo } from "react";
import type { components } from "@/shared/lib/api.types";
import { useDialogState } from "@/shared/lib/useDialogState";
import { RaidDialog } from "@/shared/raid-day/RaidDialog";
import { dayRange, todayIso } from "@/shared/utils/date";
import type { AbsenceDateRange } from "../attendance.types";
import { AbsenceDialog } from "./AbsenceDialog";
import { FloatingAbsenceTooltip } from "./FloatingAbsenceTooltip";
import { GridTable } from "./GridTable";
import { type CellAbsence, useAbsenceTooltip } from "./useAbsenceTooltip";

type Overview = components["schemas"]["OverviewResponse"];
type Raid = components["schemas"]["RaidCell"];

export function AttendanceGrid({
  overview,
  startIso,
  days,
  canManageRaids,
  onCreateOwn,
}: {
  overview: Overview;
  startIso: string;
  days: number;
  canManageRaids: boolean;
  // Opens the shared create-absence dialog (owned by OverviewPage), preselected to a date range.
  onCreateOwn: (range: AbsenceDateRange) => void;
}) {
  // Edit-absence and raid-day dialogs (create-absence is owned by OverviewPage). Target is kept during
  // the close animation (see useDialogState).
  const edit = useDialogState<CellAbsence>();
  const raid = useDialogState<{ date: string; raid?: Raid }>();

  const { tooltip, show } = useAbsenceTooltip();

  // Memoized so the cursor-following tooltip's re-renders don't defeat GridTable's memo.
  const dates = useMemo(() => dayRange(startIso, days), [startIso, days]);
  // Players arrive pre-sorted from the API (raid-role order, then class, then display name/username).
  const players = useMemo(() => overview.players ?? [], [overview.players]);
  // Keyed by ISO date. `present` is number|string (OpenAPI int32 mapping), so coerce it.
  const presentByDate = useMemo(
    () =>
      new Map(
        (overview.presentCounts ?? []).map((c) => [c.date, Number(c.present)]),
      ),
    [overview.presentCounts],
  );
  // Keyed by ISO date for O(1) lookup in the grid cells.
  const raidsByDate = useMemo(
    () => new Map((overview.raids ?? []).map((r) => [r.date, r])),
    [overview.raids],
  );
  const today = todayIso();

  // Stable identity so this prop doesn't defeat GridTable's memo.
  const onHeaderClick = useCallback(
    (date: string, r?: Raid) => raid.openWith({ date, raid: r }),
    [raid.openWith],
  );

  return (
    <>
      <GridTable
        players={players}
        dates={dates}
        today={today}
        presentByDate={presentByDate}
        raidsByDate={raidsByDate}
        canManage={canManageRaids}
        onHeaderClick={onHeaderClick}
        onEditOwn={edit.openWith}
        onCreateOwn={onCreateOwn}
        onShow={show}
      />
      {tooltip && (
        <FloatingAbsenceTooltip key={tooltip.absence.id} {...tooltip} />
      )}
      <AbsenceDialog
        open={edit.open}
        onOpenChange={edit.setOpen}
        absence={edit.target ?? undefined}
      />
      <RaidDialog
        open={raid.open}
        onOpenChange={raid.setOpen}
        date={raid.target?.date ?? today}
        raid={raid.target?.raid}
      />
    </>
  );
}
